/**
 * =============================================================================
 * UnifiedCustomHtmlRenderer — ShadowDOM reader (one of TWO EPUB renderers)
 * =============================================================================
 *
 * SIBLING: UnifiedEpubRenderer.tsx (epub.js-based).
 * ROUTING: BookReaderPage.tsx picks based on book.rendererType.
 *
 * When is THIS renderer used?
 *   For "simple" reflowable EPUBs — novels, textbooks, articles. The backend
 *   pre-extracts each chapter to HTML (see BooksExtnAppService.cs around the
 *   RewriteImagePathsInHtml / RewriteCssLinksInHtml pipeline), and this
 *   component renders one chapter at a time inside a ShadowDOM boundary.
 *   Intended share: ~90-95% of the corpus (see DrmQuotas/README.md).
 *
 * Limitations vs. epub.js renderer (keep in mind):
 *   - No fixed-layout support (pre-paginated graphic novels, picture books).
 *   - No EPUB3 interactive scripts, no media overlays (audio-text sync).
 *   - No CFI — location is (chapterIndex + scroll-anchor), not passage-precise.
 *   - No RTL/vertical-text reflow modes.
 *   Books that need those features are routed to UnifiedEpubRenderer instead.
 *
 * Current state:
 *   Every new book is forced to this renderer at upload time until the
 *   EpubComplexityAnalyzer (Sprint 4.2, stub today) is finished. That's why
 *   this is the code path you'll hit for ~100% of books right now.
 *
 * ⚠️  WHEN ADDING FEATURES, UPDATE BOTH RENDERERS ⚠️
 *   Any reader feature (layout controls, highlights, bookmarks, search, TTS,
 *   settings, etc.) must be wired into BOTH this file and
 *   UnifiedEpubRenderer.tsx, or the experience diverges between book types.
 *   Same goes for NavigationState/ReaderSettings type changes.
 *
 * Note on Spread mode: because content lives inside a ShadowDOM, CSS columns
 * applied from outside don't flow across the shadow boundary. Spread is
 * implemented by passing `columnCount` into ShadowDOMContentRenderer, which
 * applies it to .content-container INSIDE the shadow root via a CSS custom
 * property (no shadow DOM rebuild, live update).
 * =============================================================================
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UnifiedReaderLayout } from './index';
import type {
  NavigationState,
  NavigationCallbacks,
  ReaderActions,
  TOCItem,
  Bookmark,
  Note,
  SearchResult,
  HighlightColor,
} from './types';
import { BookFormat } from './types';
import LoadingSpinner from '../LoadingSpinner';
import { useBookMetadata } from '../../hooks/useBooks';
import { useBookmarks } from '../../hooks/useBookmarks';
import { useHighlights } from '../../hooks/useHighlights';
import { useReadingProgress } from '../../hooks/useReadingProgress';
import { useReaderSettings } from '../../hooks/useReaderSettings';
import { useTextAnchorSelection } from '../../hooks/useTextAnchorSelection';
import { useFlashcardDecks, useFlashcardsInDeck } from '../../hooks/useFlashcards';
import { apiClient } from '../../api/apiClient';
import { SecureReadingProvider, useSecureReading } from '../../contexts/SecureReadingContext';
import { useDrmProtection } from '../../hooks/useDrmProtection';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { offlineContentService } from '../../services/offline/OfflineContentService';
import TextSelectionMenuWithFlashcard from './TextSelectionMenuWithFlashcard';
import HighlightNoteModal from './HighlightNoteModal';
import ShadowDOMContentRenderer from './ShadowDOMContentRenderer';
import { PageTransition } from './PageTransition';
import { LayoutMode, PageAnimation, PageMargin } from './types';
import { useAlert } from '../../contexts/AlertContext';
import StudyToolsFAB from './StudyToolsFAB';
import {
  applyHighlightsToContainer,
  handleHighlightClick as handleHighlightClickUtil,
  isTextAnchorLocation,
} from '../../utils/highlightRendererWithAnchors';
import { deserializeTextAnchor } from '../../utils/textAnchor';
import { useAbpPermissions } from '../../contexts/AbpPermissionsContext';
import { booksService } from '../../api/booksService';
import { useActiveChunkIdTracker, makeChunkKey } from './useActiveChunkIdTracker';

interface Chapter {
  id: string;
  title: string;
  content: string;
  order: number;
}

interface UnifiedCustomHtmlRendererProps {
  bookId: string;
  isDrmEnabled?: boolean;
}

/**
 * Calculate the character offset of a node+offset within a container
 * This walks through all text nodes and sums their lengths to get the actual offset
 * Handles duplicate text correctly by using the actual DOM position
 */
function getCharacterOffsetWithin(node: Node, offset: number, container: Element): number {
  let charCount = 0;
  const treeWalker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  let currentNode: Node | null = null;
  while ((currentNode = treeWalker.nextNode())) {
    if (currentNode === node) {
      // Found the target node - return accumulated count + offset within node
      return charCount + offset;
    }
    // Add this node's text length to the count
    charCount += currentNode.textContent?.length || 0;
  }

  // If we didn't find the node, return -1 to indicate error
  return -1;
}

/**
 * Extract CSS and HTML separately from chapter content
 * Prevents EPUB styles from bleeding into application UI
 */
function extractCSSAndHTML(htmlContent: string): { html: string; css: string; stylesheetUrls: string[] } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  // Extract all <style> tags
  const styleElements = doc.querySelectorAll('style');
  let extractedCSS = '';
  styleElements.forEach((style) => {
    extractedCSS += style.textContent || '';
    style.remove(); // Remove from HTML
  });

  // Extract all <link rel="stylesheet"> URLs
  const linkElements = doc.querySelectorAll('link[rel="stylesheet"]');
  const stylesheetUrls: string[] = [];
  linkElements.forEach((link) => {
    const href = link.getAttribute('href');
    if (href) {
      // Store the URL for loading in Shadow DOM
      stylesheetUrls.push(href);
      console.log('📄 External stylesheet detected:', href);
    }
    link.remove(); // Remove from HTML (will be loaded separately in Shadow DOM)
  });

  // Return cleaned HTML, extracted CSS, and stylesheet URLs
  return {
    html: doc.body.innerHTML || htmlContent,
    css: extractedCSS,
    stylesheetUrls
  };
}

/**
 * Normalize text for comparison (collapses whitespace, trims)
 */
function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Calculate text similarity (simple Levenshtein-like ratio)
 * Returns a value between 0 (completely different) and 1 (identical)
 */
function textSimilarity(str1: string, str2: string): number {
  const norm1 = normalizeText(str1);
  const norm2 = normalizeText(str2);

  if (norm1 === norm2) return 1.0;

  const maxLength = Math.max(norm1.length, norm2.length);
  if (maxLength === 0) return 1.0;

  // Count matching characters (simple approach)
  let matches = 0;
  const minLength = Math.min(norm1.length, norm2.length);
  for (let i = 0; i < minLength; i++) {
    if (norm1[i] === norm2[i]) matches++;
  }

  return matches / maxLength;
}

/**
 * Fuzzy search for text within a larger string
 * Returns the best matching offset or -1 if not found
 */
function fuzzyFindText(haystack: string, needle: string, startHint: number = 0): number {
  const normNeedle = normalizeText(needle);
  const normHaystack = normalizeText(haystack);

  // First try exact match
  let index = normHaystack.indexOf(normNeedle, Math.max(0, startHint - 100));
  if (index !== -1) return index;

  // Try searching near the hint offset
  const searchRadius = 500; // Search within 500 chars of the hint
  const searchStart = Math.max(0, startHint - searchRadius);
  const searchEnd = Math.min(normHaystack.length, startHint + searchRadius + normNeedle.length);

  let bestMatch = -1;
  let bestSimilarity = 0.8; // Require at least 80% similarity

  for (let i = searchStart; i < searchEnd; i++) {
    const candidate = normHaystack.substring(i, i + normNeedle.length);
    const similarity = textSimilarity(candidate, normNeedle);

    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestMatch = i;
    }
  }

  return bestMatch;
}

/**
 * Apply highlights to Shadow DOM content using TextAnchors
 * Works with the shadowDOMAPI exposed by ShadowDOMContentRenderer
 */
function applyHighlightsToShadowDOM(
  shadowHost: HTMLElement,
  highlights: Note[],
  onHighlightClick?: (noteId: string) => void
): void {
  const api = (shadowHost as any).shadowDOMAPI;
  if (!api) {
    console.warn('Shadow DOM API not available yet');
    return;
  }

  const shadowRoot = api.getShadowRoot();
  if (!shadowRoot) {
    console.warn('Shadow root not available');
    return;
  }

  const container = shadowRoot.querySelector('.content-container');
  if (!container) {
    console.warn('Content container not found in Shadow DOM');
    return;
  }

  // Get existing highlight IDs to avoid re-applying
  const existingHighlights = container.querySelectorAll('.shadow-dom-range-highlight, .reader-highlight');
  const existingHighlightIds = new Set<string>();
  existingHighlights.forEach((el: Element) => {
    const highlightId = el.getAttribute('data-highlight-id');
    if (highlightId) {
      existingHighlightIds.add(highlightId);
    }
  });

  // Build set of highlight IDs that should exist
  const targetHighlightIds = new Set(highlights.map(note => note.id));

  // Remove highlights that are no longer in the list
  existingHighlights.forEach((el: Element) => {
    const highlightId = el.getAttribute('data-highlight-id');
    if (highlightId && !targetHighlightIds.has(highlightId)) {
      // This highlight should be removed
      const parent = el.parentNode;
      while (el.firstChild) {
        parent?.insertBefore(el.firstChild, el);
      }
      el.remove();
    }
  });

  // Apply each highlight (skip if already applied)
  highlights.forEach((note) => {
    // Skip if this highlight is already applied
    if (existingHighlightIds.has(note.id)) {
      console.log('Skipping already-applied highlight:', note.id);
      return;
    }
    try {
      // Check if it's the new shadow-dom-range JSON format
      if (note.location.startsWith('{"type":"shadow-dom-range"')) {
        const anchor = JSON.parse(note.location);
        console.log('Applying shadow-dom-range highlight:', note.id, anchor);

        // Get the full text content from the Shadow DOM container
        const textContent = container.textContent || '';
        const { startOffset, endOffset, prefix, suffix } = anchor;

        // Verify the text at the expected offsets matches (with prefix/suffix check)
        const selectedText = textContent.substring(startOffset, endOffset);
        const actualPrefix = textContent.substring(Math.max(0, startOffset - 50), startOffset);
        const actualSuffix = textContent.substring(endOffset, Math.min(textContent.length, endOffset + 50));

        // Normalize texts for comparison
        const normSelectedText = normalizeText(selectedText);
        const normNoteText = normalizeText(note.selectedText);

        // Check if prefix and suffix match (more lenient)
        const prefixMatches = actualPrefix.includes(prefix) || prefix.includes(actualPrefix) ||
                             normalizeText(actualPrefix).includes(normalizeText(prefix));
        const suffixMatches = actualSuffix.includes(suffix) || suffix.includes(actualSuffix) ||
                             normalizeText(actualSuffix).includes(normalizeText(suffix));

        // Calculate text similarity
        const similarity = textSimilarity(selectedText, note.selectedText);
        const isExactMatch = normSelectedText === normNoteText;
        const isFuzzyMatch = similarity >= 0.85; // 85% similarity threshold

        if ((isExactMatch || isFuzzyMatch) && prefixMatches && suffixMatches) {
          // Find the text node(s) that contain this offset range
          const textNodes: Text[] = [];
          const walker = shadowRoot.ownerDocument.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            null
          );

          let node;
          while ((node = walker.nextNode())) {
            if (node.nodeType === Node.TEXT_NODE && node.textContent) {
              textNodes.push(node as Text);
            }
          }

          // Calculate cumulative offsets to find ALL text nodes within range
          let cumulativeOffset = 0;
          let highlightApplied = false;
          let startNode: Text | null = null;
          let endNode: Text | null = null;
          let startNodeLocalOffset = 0;
          let endNodeLocalOffset = 0;

          // First pass: find start and end nodes
          for (const textNode of textNodes) {
            const nodeLength = textNode.textContent?.length || 0;
            const nodeStart = cumulativeOffset;
            const nodeEnd = cumulativeOffset + nodeLength;

            // Find start node
            if (startOffset >= nodeStart && startOffset < nodeEnd && !startNode) {
              startNode = textNode;
              startNodeLocalOffset = startOffset - nodeStart;
            }

            // Find end node
            if (endOffset > nodeStart && endOffset <= nodeEnd && !endNode) {
              endNode = textNode;
              endNodeLocalOffset = endOffset - nodeStart;
            }

            cumulativeOffset += nodeLength;

            if (startNode && endNode) break;
          }

          if (!startNode || !endNode) {
            console.warn('Could not find start/end nodes for highlight:', note.id);
            return;
          }

          // Check if highlight is within a single text node
          const isSingleNode = startNode === endNode;

          if (isSingleNode) {
            // Simple case: single text node highlight
            try {
              const range = shadowRoot.ownerDocument.createRange();
              range.setStart(startNode, startNodeLocalOffset);
              range.setEnd(endNode, endNodeLocalOffset);

              const highlight = shadowRoot.ownerDocument.createElement('span');
              highlight.className = 'shadow-dom-range-highlight';
              highlight.style.backgroundColor = note.color;
              highlight.style.cursor = 'pointer';
              highlight.setAttribute('data-highlight-id', note.id);
              highlight.setAttribute('data-note-text', note.noteText || '');
              highlight.setAttribute('data-highlight-color', note.color);

              // Add click handler to open edit dialog
              if (onHighlightClick) {
                highlight.addEventListener('click', (e) => {
                  e.stopPropagation();
                  console.log('🖱️ Highlight clicked:', {
                    noteId: note.id,
                    highlightsCount: highlights.length
                  });
                  // Pass note ID to parent, which will look up current note from fresh state
                  onHighlightClick(note.id);
                });
              }

              range.surroundContents(highlight);
              console.log('Successfully applied shadow-dom-range highlight (single node):', note.id);
              highlightApplied = true;
            } catch (e) {
              console.warn('Failed to apply single-node highlight:', e);
            }
          } else {
            // Complex case: multi-node highlight
            // Reset cumulative offset and apply highlights to all nodes in range
            cumulativeOffset = 0;
            let inRange = false;

            for (const textNode of textNodes) {
              const nodeLength = textNode.textContent?.length || 0;
              const nodeStart = cumulativeOffset;
              const nodeEnd = cumulativeOffset + nodeLength;

              // Check if we're at the start node
              if (textNode === startNode) {
                inRange = true;
              }

              if (inRange) {
                // Calculate the portion of this text node that should be highlighted
                let localStartOffset = 0;
                let localEndOffset = nodeLength;

                if (textNode === startNode) {
                  localStartOffset = startNodeLocalOffset;
                }
                if (textNode === endNode) {
                  localEndOffset = endNodeLocalOffset;
                }

                // Create range for this text node portion
                try {
                  const range = shadowRoot.ownerDocument.createRange();
                  range.setStart(textNode, localStartOffset);
                  range.setEnd(textNode, localEndOffset);

                  const highlight = shadowRoot.ownerDocument.createElement('span');
                  highlight.className = 'shadow-dom-range-highlight';
                  highlight.style.backgroundColor = note.color;
                  highlight.style.cursor = 'pointer';
                  highlight.setAttribute('data-highlight-id', note.id);
                  highlight.setAttribute('data-note-text', note.noteText || '');
                  highlight.setAttribute('data-highlight-color', note.color);

                  // Add click handler to open edit dialog
                  if (onHighlightClick) {
                    highlight.addEventListener('click', (e) => {
                      e.stopPropagation();
                      onHighlightClick(note.id);
                    });
                  }

                  range.surroundContents(highlight);
                  highlightApplied = true;
                } catch (e) {
                  console.warn('Failed to apply multi-node highlight portion:', e);
                }
              }

              // Check if we've reached the end node
              if (textNode === endNode) {
                break;
              }

              cumulativeOffset += nodeLength;
            }

            if (highlightApplied) {
              console.log('Successfully applied shadow-dom-range highlight (multi-node):', note.id);
            }
          }
        } else {
          // Fallback: Try fuzzy search for the text
          console.log('Exact offset mismatch, attempting fuzzy search for highlight:', note.id);

          const fuzzyOffset = fuzzyFindText(textContent, note.selectedText, startOffset);

          if (fuzzyOffset !== -1) {
            console.log(`Found highlight via fuzzy search at offset ${fuzzyOffset} (original: ${startOffset})`);

            // Apply highlight at the fuzzy-matched offset
            const fuzzyEndOffset = fuzzyOffset + note.selectedText.length;

            // Find the text nodes for the fuzzy match
            const textNodes: Text[] = [];
            const walker = shadowRoot.ownerDocument.createTreeWalker(
              container,
              NodeFilter.SHOW_TEXT,
              null
            );

            let node;
            while ((node = walker.nextNode())) {
              if (node.nodeType === Node.TEXT_NODE && node.textContent) {
                textNodes.push(node as Text);
              }
            }

            // Calculate cumulative offsets for fuzzy range (same logic as above)
            let cumulativeOffset = 0;
            let fuzzyHighlightApplied = false;
            let fuzzyStartNode: Text | null = null;
            let fuzzyEndNode: Text | null = null;
            let fuzzyStartNodeLocalOffset = 0;
            let fuzzyEndNodeLocalOffset = 0;

            // First pass: find start and end nodes for fuzzy match
            for (const textNode of textNodes) {
              const nodeLength = textNode.textContent?.length || 0;
              const nodeStart = cumulativeOffset;
              const nodeEnd = cumulativeOffset + nodeLength;

              // Find start node
              if (fuzzyOffset >= nodeStart && fuzzyOffset < nodeEnd && !fuzzyStartNode) {
                fuzzyStartNode = textNode;
                fuzzyStartNodeLocalOffset = fuzzyOffset - nodeStart;
              }

              // Find end node
              if (fuzzyEndOffset > nodeStart && fuzzyEndOffset <= nodeEnd && !fuzzyEndNode) {
                fuzzyEndNode = textNode;
                fuzzyEndNodeLocalOffset = fuzzyEndOffset - nodeStart;
              }

              cumulativeOffset += nodeLength;

              if (fuzzyStartNode && fuzzyEndNode) break;
            }

            if (!fuzzyStartNode || !fuzzyEndNode) {
              console.warn('Could not find start/end nodes for fuzzy highlight:', note.id);
              return;
            }

            // Check if highlight is within a single text node
            const isSingleNode = fuzzyStartNode === fuzzyEndNode;

            if (isSingleNode) {
              // Simple case: single text node highlight
              try {
                const range = shadowRoot.ownerDocument.createRange();
                range.setStart(fuzzyStartNode, fuzzyStartNodeLocalOffset);
                range.setEnd(fuzzyEndNode, fuzzyEndNodeLocalOffset);

                const highlight = shadowRoot.ownerDocument.createElement('span');
                highlight.className = 'shadow-dom-range-highlight';
                highlight.style.backgroundColor = note.color;
                highlight.style.cursor = 'pointer';
                highlight.setAttribute('data-highlight-id', note.id);
                highlight.setAttribute('data-note-text', note.noteText || '');
                highlight.setAttribute('data-highlight-color', note.color);

                // Add click handler to open edit dialog
                if (onHighlightClick) {
                  highlight.addEventListener('click', (e) => {
                    e.stopPropagation();
                    onHighlightClick(note.id);
                  });
                }

                range.surroundContents(highlight);
                console.log('Successfully applied fuzzy highlight (single node):', note.id);
                fuzzyHighlightApplied = true;
              } catch (e) {
                console.warn('Failed to apply single-node fuzzy highlight:', e);
              }
            } else {
              // Complex case: multi-node highlight
              cumulativeOffset = 0;
              let inRange = false;

              for (const textNode of textNodes) {
                const nodeLength = textNode.textContent?.length || 0;
                const nodeStart = cumulativeOffset;
                const nodeEnd = cumulativeOffset + nodeLength;

                if (textNode === fuzzyStartNode) {
                  inRange = true;
                }

                if (inRange) {
                  let localStartOffset = 0;
                  let localEndOffset = nodeLength;

                  if (textNode === fuzzyStartNode) {
                    localStartOffset = fuzzyStartNodeLocalOffset;
                  }
                  if (textNode === fuzzyEndNode) {
                    localEndOffset = fuzzyEndNodeLocalOffset;
                  }

                  try {
                    const range = shadowRoot.ownerDocument.createRange();
                    range.setStart(textNode, localStartOffset);
                    range.setEnd(textNode, localEndOffset);

                    const highlight = shadowRoot.ownerDocument.createElement('span');
                    highlight.className = 'shadow-dom-range-highlight';
                    highlight.style.backgroundColor = note.color;
                    highlight.style.cursor = 'pointer';
                    highlight.setAttribute('data-highlight-id', note.id);
                    highlight.setAttribute('data-note-text', note.noteText || '');
                    highlight.setAttribute('data-highlight-color', note.color);

                    // Add click handler to open edit dialog
                    if (onHighlightClick) {
                      highlight.addEventListener('click', (e) => {
                        e.stopPropagation();
                        onHighlightClick(note.id);
                      });
                    }

                    range.surroundContents(highlight);
                    fuzzyHighlightApplied = true;
                  } catch (e) {
                    console.warn('Failed to apply multi-node fuzzy highlight portion:', e);
                  }
                }

                if (textNode === fuzzyEndNode) {
                  break;
                }

                cumulativeOffset += nodeLength;
              }

              if (fuzzyHighlightApplied) {
                console.log('Successfully applied fuzzy highlight (multi-node):', note.id);
              }
            }
          } else {
            console.warn('Shadow-dom-range highlight not found (fuzzy search failed):', {
              noteId: note.id,
              expected: note.selectedText,
              found: selectedText,
              similarity,
              prefixMatches,
              suffixMatches
            });
          }
        }

      } else if (isTextAnchorLocation(note.location)) {
        // Legacy TextAnchor format support
        console.log('Applying TextAnchor highlight:', note.id);

        const anchor = deserializeTextAnchor(note.location as string);
        if (!anchor) {
          console.warn('Failed to deserialize TextAnchor for highlight:', note.id);
          return;
        }

        // Find the text node using prefix/suffix matching
        const textNodes: Text[] = [];
        const walker = shadowRoot.ownerDocument.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null
        );

        let node;
        while ((node = walker.nextNode())) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent) {
            textNodes.push(node as Text);
          }
        }

        // Find matching text using prefix and suffix
        for (const textNode of textNodes) {
          const text = textNode.textContent || '';
          const matchIndex = text.indexOf(note.selectedText);

          if (matchIndex !== -1) {
            // Verify prefix and suffix match
            const prefix = text.substring(Math.max(0, matchIndex - anchor.prefix.length), matchIndex);
            const suffix = text.substring(matchIndex + note.selectedText.length, matchIndex + note.selectedText.length + anchor.suffix.length);

            if (prefix === anchor.prefix && suffix === anchor.suffix) {
              // Found the correct match - create highlight
              const range = shadowRoot.ownerDocument.createRange();
              range.setStart(textNode, matchIndex);
              range.setEnd(textNode, matchIndex + note.selectedText.length);

              const highlight = shadowRoot.ownerDocument.createElement('span');
              highlight.className = 'reader-highlight';
              highlight.style.backgroundColor = note.color;
              highlight.style.cursor = 'pointer';
              highlight.setAttribute('data-highlight-id', note.id);
              highlight.setAttribute('data-note-text', note.noteText || '');

              // Add click handler to open edit dialog
              if (onHighlightClick) {
                highlight.addEventListener('click', (e) => {
                  e.stopPropagation();
                  console.log('🖱️ Highlight clicked:', {
                    noteId: note.id,
                    highlightsCount: highlights.length
                  });
                  // Pass note ID to parent, which will look up current note from fresh state
                  onHighlightClick(note.id);
                });
              }

              try {
                range.surroundContents(highlight);
                console.log('Successfully applied TextAnchor highlight:', note.id);
              } catch (e) {
                console.warn('Failed to apply highlight, trying alternative method:', e);

                // Alternative: Insert highlight markers
                const startMarker = shadowRoot.ownerDocument.createElement('span');
                startMarker.className = 'reader-highlight';
                startMarker.style.backgroundColor = note.color;
                startMarker.style.cursor = 'pointer';
                startMarker.setAttribute('data-highlight-id', note.id);
                startMarker.textContent = range.toString();

                // Add click handler to open edit dialog
                if (onHighlightClick) {
                  startMarker.addEventListener('click', (e) => {
                    e.stopPropagation();
                    // Pass note ID to parent, which will look up current note from fresh state
                    onHighlightClick(note.id);
                  });
                }

                range.deleteContents();
                range.insertNode(startMarker);
              }

              break; // Found and applied, move to next highlight
            }
          }
        }
      } else {
        console.warn('Unknown highlight location format:', note.id, note.location);
      }
    } catch (error) {
      console.error('Error applying highlight:', note.id, error);
    }
  });
}

/**
 * Internal component that contains the actual CustomHTML reader logic
 */
function UnifiedCustomHtmlRendererInternal({
  bookId,
  isDrmEnabled = false,
}: UnifiedCustomHtmlRendererProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useAlert();

  // DRM session management (only if DRM is enabled)
  const drmContext = isDrmEnabled ? useSecureReading() : null;

  // Apply DRM protections when DRM is enabled
  // Note: We allow text selection for highlights/notes, but disable copy/paste via keyboard shortcuts
  useDrmProtection({
    disableRightClick: isDrmEnabled,
    disableTextSelection: false, // Allow selection for highlights/notes
    disableKeyboardShortcuts: isDrmEnabled,
    detectDevTools: isDrmEnabled,
    disablePrint: isDrmEnabled,
    onDevToolsDetected: isDrmEnabled ? () => {
      console.error('DevTools detected! Navigating away...');
      navigate('/');
    } : undefined,
    onViolationAttempt: isDrmEnabled ? (type) => {
      console.warn(`DRM violation attempt: ${type}`);
    } : undefined,
  });

  // Initialize DRM session if enabled
  useEffect(() => {
    if (isDrmEnabled && drmContext && !drmContext.session) {
      drmContext.initiateSession(bookId).catch((err) => {
        console.error('Failed to initiate DRM session:', err);
      });
    }
  }, [isDrmEnabled, bookId]);
  // Note: drmContext is NOT in dependencies to prevent re-initialization when
  // callback references change during fingerprinting completion

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const loadingChaptersRef = useRef<Set<number>>(new Set()); // Track chapters currently being loaded
  const isLoadingChapterListRef = useRef(false); // Track if chapter list is being loaded

  // CustomHTML-specific state
  const [chapters, setChapters] = useState<Chapter[]>([]);
  // Start with -1 to indicate "not initialized" - will be set by restore position effect
  // This prevents loading chapter 0 content before we know the actual starting chapter
  const [currentChapterIndex, setCurrentChapterIndex] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [tableOfContents, setTableOfContents] = useState<TOCItem[]>([]);
  const [isShadowDOMReady, setIsShadowDOMReady] = useState(false);

  // Tracks the SecureContentChunkId (Guid) of the chapter the user is currently
  // viewing. The `/chapter-list` endpoint returns ChunkIndex but not the Guid,
  // so the tracker below is populated opportunistically from `/search-in-book`
  // responses (which DO include `secureContentChunkId`). When the user hasn't
  // searched yet, the Library tab shows its empty-state placeholder.
  const chunkIdTracker = useActiveChunkIdTracker();
  const activeSecureContentChunkId = useMemo(() => {
    if (currentChapterIndex < 0) return null;
    const key = makeChunkKey('chapter', currentChapterIndex);
    return chunkIdTracker.resolveForKey(key);
  }, [currentChapterIndex, chunkIdTracker.mapping, chunkIdTracker.resolveForKey]);

  // Track if we've restored the initial reading position (should only happen once)
  const hasRestoredPositionRef = useRef(false);

  // Fetch book metadata
  const { data: metadata } = useBookMetadata(bookId);

  // Fetch bookmarks and highlights from backend
  const {
    bookmarks,
    createBookmark,
    deleteBookmark,
    updateBookmark,
    isCreating: isCreatingBookmark,
  } = useBookmarks(bookId);

  const {
    highlights: notes,
    createHighlight,
    deleteHighlight,
    updateHighlight,
    createPageNote,
    isCreatingPageNote,
  } = useHighlights(bookId);

  // Reading progress with auto-save
  // Get ABP settings (including progress save interval)
  const { getSetting } = useAbpPermissions();
  const progressSaveInterval = parseInt(
    getSetting('learnleaf.ProgressSaveIntervalSeconds', '30'),
    10
  );

  const {
    progress: savedProgress,
    saveProgress,
    saveProgressOnPageChange,
    startAutoSave,
    stopAutoSave,
  } = useReadingProgress(bookId, progressSaveInterval);

  // Reader settings (global per user)
  const { settings } = useReaderSettings();

  // Layout preferences (custom-html variant — no epub.js rendition involved).
  // Spread mode → CSS column-count: 2. Single/Scrolling → single column (both behave the
  // same here since the renderer always flows chapter content vertically).
  const layoutMode = settings?.layoutMode ?? LayoutMode.Auto;
  const pageMargin = settings?.pageMargin ?? PageMargin.Medium;
  const pageAnimation = settings?.pageAnimation ?? PageAnimation.Slide;
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1024 : window.innerWidth
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let t: number | undefined;
    const onResize = () => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => setViewportWidth(window.innerWidth), 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      if (t) window.clearTimeout(t);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  const effectiveLayout: LayoutMode.Single | LayoutMode.Spread | LayoutMode.Scrolling = (() => {
    if (layoutMode === LayoutMode.Scrolling) return LayoutMode.Scrolling;
    if (layoutMode === LayoutMode.Single) return LayoutMode.Single;
    if (layoutMode === LayoutMode.Spread) return viewportWidth >= 900 ? LayoutMode.Spread : LayoutMode.Single;
    return viewportWidth >= 900 ? LayoutMode.Spread : LayoutMode.Single;
  })();
  const marginPadding =
    pageMargin === PageMargin.Narrow ? '20px'
    : pageMargin === PageMargin.Wide ? '80px'
    : '40px';
  // Flashcard decks for this book
  const { decks, createDeck } = useFlashcardDecks(bookId);
  const [currentDeckId, setCurrentDeckId] = useState<string | null>(null);
  const { createFlashcard } = useFlashcardsInDeck(currentDeckId);

  // Text selection with TextAnchors (for main document - not used for Shadow DOM)
  const {
    selectedText: mainDocSelectedText,
    textAnchor: mainDocTextAnchor,
    menuPosition: mainDocMenuPosition,
    clearSelection: clearMainDocSelection,
    getSerializedAnchor: getMainDocSerializedAnchor,
  } = useTextAnchorSelection(containerRef);

  // Shadow DOM selection state (managed manually)
  const [shadowSelectedText, setShadowSelectedText] = useState<string>('');
  const [shadowRange, setShadowRange] = useState<Range | null>(null);
  const [shadowMenuPosition, setShadowMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [shadowSerializedAnchor, setShadowSerializedAnchor] = useState<string | null>(null);

  // Unified selection state (use Shadow DOM if available, fallback to main doc)
  const selectedText = shadowSelectedText || mainDocSelectedText;
  const textAnchor = shadowSelectedText ? null : mainDocTextAnchor; // TextAnchor only for main doc
  const menuPosition = shadowMenuPosition || mainDocMenuPosition;

  const clearSelection = useCallback(() => {
    // Remove temporary highlight from Shadow DOM
    const hostElement = shadowHostRef.current;
    if (hostElement) {
      const shadowDOMAPI = (hostElement as any).shadowDOMAPI;
      const shadowRoot = shadowDOMAPI?.getShadowRoot();
      if (shadowRoot) {
        const contentContainer = shadowRoot.querySelector('.content-container');
        if (contentContainer) {
          const tempHighlight = contentContainer.querySelector('.temp-selection-highlight');
          if (tempHighlight) {
            // Unwrap the temporary highlight
            const parent = tempHighlight.parentNode;
            while (tempHighlight.firstChild) {
              parent?.insertBefore(tempHighlight.firstChild, tempHighlight);
            }
            parent?.removeChild(tempHighlight);
            console.log('Removed temporary selection highlight');
          }
        }
      }
    }

    // Clear Shadow DOM selection
    setShadowSelectedText('');
    setShadowRange(null);
    setShadowMenuPosition(null);
    setShadowSerializedAnchor(null);

    // Clear main doc selection
    clearMainDocSelection();
  }, [clearMainDocSelection]);

  const getSerializedAnchor = useCallback((): string | null => {
    // For Shadow DOM selections, use pre-calculated anchor
    if (shadowSerializedAnchor) {
      console.log('Using Shadow DOM anchor:', shadowSerializedAnchor);
      return shadowSerializedAnchor;
    }

    // Fallback to main doc anchor
    const mainAnchor = getMainDocSerializedAnchor();
    console.log('Using main doc anchor:', mainAnchor ? 'found' : 'null');
    return mainAnchor;
  }, [shadowSerializedAnchor, getMainDocSerializedAnchor]);

  // Highlight note modal state
  const [selectedHighlight, setSelectedHighlight] = useState<Note | null>(null);

  // Load chapter list (metadata only) from backend - FAST!
  useEffect(() => {
    const loadChapterList = async () => {
      // Prevent duplicate requests (React StrictMode protection)
      if (isLoadingChapterListRef.current) {
        return;
      }

      try {
        isLoadingChapterListRef.current = true;
        setIsLoading(true);

        // `secureContentChunkId` is the real Guid for each chapter's
        // SecureContentChunk row; `id` on this DTO is ChunkIndex.ToString()
        // (legacy). The Guid is what the Flashcard Library tab needs, so we
        // harvest it into chunkIdTracker right after the fetch.
        let chapterMetadata: Array<{id: string; title: string; order: number; secureContentChunkId?: string}> = [];

        // Try to fetch from API first
        try {
          const response = await apiClient.get<Array<{id: string; title: string; order: number; secureContentChunkId?: string}>>(
            `/api/app/books-extn/chapter-list/${bookId}`
          );
          chapterMetadata = response.data.sort((a, b) => a.order - b.order);
          console.log('📡 Loaded chapter list from API');

          // Pre-populate the tracker so the Flashcard Library tab works
          // immediately after book open, without requiring a backend search.
          chunkIdTracker.harvestChapterList(chapterMetadata, (meta) =>
            makeChunkKey('chapter', meta.order)
          );
        } catch (apiError) {
          console.log('⚠️ API chapter list failed, trying offline storage...');

          // API failed - try offline storage
          if (user?.sub) {
            const offlineTOC = await offlineContentService.getEpubTOC(bookId);

            if (offlineTOC.length > 0) {
              chapterMetadata = offlineTOC.map(chapter => ({
                id: chapter.index.toString(),
                title: chapter.title,
                order: chapter.index,
              }));
              console.log('💾 Loaded chapter list from offline storage');
            } else {
              throw new Error('No chapters available offline');
            }
          } else {
            throw apiError;
          }
        }

        // Initialize chapters array with placeholders (content will be loaded on-demand)
        const chapterPlaceholders: Chapter[] = chapterMetadata.map((meta) => ({
          id: meta.id,
          title: meta.title,
          content: '', // Empty initially - loaded on demand
          order: meta.order,
        }));
        setChapters(chapterPlaceholders);

        // Generate TOC from chapter metadata
        const toc: TOCItem[] = chapterMetadata.map((meta, index) => ({
          id: meta.id,
          label: meta.title,
          href: `#chapter-${index}`,
          pageNumber: index,
          level: 0,
        }));
        setTableOfContents(toc);

        // Don't set currentChapterIndex here - let the restore position effect handle it
        // This prevents loading chapter 0 before we know the actual starting position
      } catch (error) {
        console.error('Failed to load chapter list:', error);
        // Fallback: Create mock chapters for development
        const mockChapters: Chapter[] = [
          {
            id: '1',
            title: 'Error!',
            content: '<h1>Book not available</h1><p>This book is not available offline. Please connect to the internet or download it first.</p>',
            order: 0,
          },
        ];
        setChapters(mockChapters);
        setTableOfContents([
          { id: '1', label: 'Error', level: 0, pageNumber: 0 },
        ]);
      } finally {
        setIsLoading(false);
        isLoadingChapterListRef.current = false;
      }
    };

    loadChapterList();
  }, [bookId, user?.sub]);

  // Load chapter content on-demand when user navigates
  useEffect(() => {
    const loadChapterContent = async () => {
      // Don't load if chapters aren't ready or index hasn't been initialized yet
      if (chapters.length === 0 || currentChapterIndex < 0) return;

      const chapter = chapters[currentChapterIndex];
      if (!chapter) return;

      // Check if content is already loaded
      if (chapter.content && chapter.content.trim() !== '') {
        return; // Already loaded, skip
      }

      // Check if this chapter is already being loaded (prevents duplicate requests during React StrictMode)
      if (loadingChaptersRef.current.has(currentChapterIndex)) {
        return; // Already loading, skip
      }

      // Mark chapter as loading
      loadingChaptersRef.current.add(currentChapterIndex);

      try {
        let chapterData: Chapter | null = null;

        // Try to load from API first (online mode)
        try {
          const response = await apiClient.get<Chapter>(
            `/api/app/books-extn/chapters/${bookId}/${currentChapterIndex}`
          );
          chapterData = response.data;
          console.log(`📡 Loaded chapter ${currentChapterIndex} from API: ${response.data.title}`);
        } catch (apiError) {
          console.log(`⚠️ API failed for chapter ${currentChapterIndex}, trying offline storage...`);

          // API failed - try to load from offline storage
          if (user?.sub) {
            console.log(`👤 User ID: ${user.sub}, BookID: ${bookId}, ChapterIndex: ${currentChapterIndex}`);
            const offlineChapter = await offlineContentService.getEpubChapter(
              bookId,
              currentChapterIndex,
              user.sub
            );

            if (offlineChapter) {
              chapterData = {
                id: offlineChapter.chapterIndex.toString(),
                title: offlineChapter.title,
                content: offlineChapter.htmlContent,
                order: offlineChapter.chapterIndex,
              };
              console.log(`💾 Loaded chapter ${currentChapterIndex} from offline storage: ${offlineChapter.title}`);
            } else {
              console.error(`❌ Chapter ${currentChapterIndex} not available offline`);
              throw new Error('Chapter not available offline. Please download it first or connect to the internet.');
            }
          } else {
            // User not logged in, can't access offline storage
            throw apiError;
          }
        }

        if (chapterData) {
          // Update chapters array with loaded content
          setChapters((prevChapters) => {
            const updatedChapters = [...prevChapters];
            updatedChapters[currentChapterIndex] = {
              ...chapterData,
              order: currentChapterIndex,
            };
            return updatedChapters;
          });
        }
      } catch (error) {
        console.error(`Failed to load chapter ${currentChapterIndex}:`, error);
        // Optionally show user-friendly error message
        showToast(`Failed to load chapter ${currentChapterIndex}. ${error instanceof Error ? error.message : 'Please try again.'}`, { type: 'error' });
      } finally {
        // Remove from loading set when done
        loadingChaptersRef.current.delete(currentChapterIndex);
      }
    };

    loadChapterContent();
  }, [currentChapterIndex, bookId, user?.sub]); // Added user.sub for offline access

  // Restore last reading position (only once on initial load)
  useEffect(() => {
    // Only restore position once
    if (hasRestoredPositionRef.current) {
      return; // Already restored, skip all subsequent runs
    }

    // Wait for chapters to be loaded
    if (chapters.length === 0) {
      return; // Not ready yet, wait for chapters
    }

    // Determine starting chapter index
    let startingChapterIndex = 0; // Default to first chapter

    if (savedProgress) {
      // Restore the saved position
      const savedChapterIndex =
        typeof savedProgress.currentLocation === 'number'
          ? savedProgress.currentLocation
          : parseInt(String(savedProgress.currentLocation), 10);

      if (savedChapterIndex >= 0 && savedChapterIndex < chapters.length) {
        startingChapterIndex = savedChapterIndex;
      }
    }

    // Set the initial chapter index (this will trigger content loading)
    setCurrentChapterIndex(startingChapterIndex);

    // Mark as restored to prevent future runs (including when auto-save updates savedProgress)
    hasRestoredPositionRef.current = true;
  }, [chapters, savedProgress]); // Depend on both, but ref prevents re-runs after first restore

  // Get current chapter
  const currentChapter = useMemo(
    () => chapters[currentChapterIndex],
    [chapters, currentChapterIndex]
  );

  // Extract CSS and HTML from current chapter (memoized for performance)
  const { html: chapterHTML, css: chapterCSS, stylesheetUrls: chapterStylesheetUrls } = useMemo(() => {
    if (!currentChapter) return { html: '', css: '', stylesheetUrls: [] };
    return extractCSSAndHTML(currentChapter.content);
  }, [currentChapter]);

  // Start auto-save and cleanup on unmount
  useEffect(() => {
    const cleanup = startAutoSave(
      () => currentChapterIndex,
      () => currentChapter?.title || `Chapter ${currentChapterIndex + 1}`,
      () => (chapters.length ? ((currentChapterIndex + 1) / chapters.length) * 100 : 0)
    );

    return cleanup;
  }, [startAutoSave, currentChapterIndex, currentChapter, chapters]);

  // Save progress immediately when chapter changes
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip on initial mount (we just loaded the saved progress)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (chapters.length > 0 && currentChapter) {
      saveProgressOnPageChange(
        currentChapterIndex,
        currentChapter.title || `Chapter ${currentChapterIndex + 1}`,
        ((currentChapterIndex + 1) / chapters.length) * 100
      );
    }
  }, [currentChapterIndex]); // Only depend on currentChapterIndex

  // Auto-select or create default deck
  useEffect(() => {
    if (decks.length > 0 && !currentDeckId) {
      // Use first deck by default
      setCurrentDeckId(decks[0].id);
    }
  }, [decks, currentDeckId]);

  // Reset Shadow DOM ready state when chapter changes
  useEffect(() => {
    setIsShadowDOMReady(false);
  }, [currentChapterIndex]);

  // Apply highlights to Shadow DOM after chapter renders
  useEffect(() => {
    if (!isShadowDOMReady || !currentChapter || !shadowHostRef.current) {
      return;
    }

    // Filter highlights for current chapter (supports both old and new formats)
    const chapterHighlights = notes.filter((note) => {
      // New format: JSON anchor with chapter index
      if (note.location.startsWith('{"type":"shadow-dom-range"')) {
        try {
          const anchor = JSON.parse(note.location);
          return anchor.chapterIndex === currentChapterIndex;
        } catch (e) {
          console.warn('Failed to parse shadow-dom-range anchor:', e);
          return false;
        }
      }

      // Check if location is TextAnchor format
      if (isTextAnchorLocation(note.location)) {
        const anchor = deserializeTextAnchor(note.location as string);
        return anchor?.chapterIndex === currentChapterIndex;
      }

      // Old format: simple chapter index
      return note.location === currentChapterIndex;
    });

    // Wait for Shadow DOM to be fully initialized
    const applyHighlights = () => {
      if (shadowHostRef.current) {
        // Wrap setSelectedHighlight to look up note by ID from fresh state
        const handleHighlightClick = (noteId: string) => {
          const note = notes.find(n => n.id === noteId);
          if (note) {
            setSelectedHighlight(note);
          } else {
            console.error('❌ Could not find note with ID:', noteId);
          }
        };
        applyHighlightsToShadowDOM(shadowHostRef.current, chapterHighlights, handleHighlightClick);
      }
    };

    // Use requestAnimationFrame to ensure Shadow DOM is ready
    const frameId = requestAnimationFrame(() => {
      // Add small delay to ensure content is rendered
      setTimeout(applyHighlights, 100);
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [currentChapter, notes, currentChapterIndex, isShadowDOMReady]);

  // Convert CustomHTML state to unified navigation state
  const navigation: NavigationState = useMemo(
    () => ({
      currentLocation: currentChapterIndex,
      currentLocationLabel: currentChapter?.title || 'Chapter 1',
      totalLocations: chapters.length || 1,
      progressPercentage: chapters.length
        ? ((currentChapterIndex + 1) / chapters.length) * 100
        : 0,
      canNavigatePrev: currentChapterIndex > 0,
      canNavigateNext: currentChapterIndex < chapters.length - 1,
    }),
    [currentChapterIndex, currentChapter, chapters]
  );

  // Navigation callbacks
  const navigationCallbacks: NavigationCallbacks = useMemo(
    () => ({
      onNavigatePrev: () => {
        if (currentChapterIndex > 0) {
          setCurrentChapterIndex((prev) => prev - 1);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      onNavigateNext: () => {
        if (currentChapterIndex < chapters.length - 1) {
          setCurrentChapterIndex((prev) => prev + 1);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      onNavigateToLocation: (location) => {
        const chapterIndex =
          typeof location === 'number' ? location : parseInt(String(location), 10);
        if (chapterIndex >= 0 && chapterIndex < chapters.length) {
          setCurrentChapterIndex(chapterIndex);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      onNavigateToTOC: (item) => {
        if (item.pageNumber !== undefined) {
          setCurrentChapterIndex(item.pageNumber);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
      onNavigateToBookmark: (bookmark) => {
        const chapterIndex =
          typeof bookmark.location === 'number' ? bookmark.location : 0;
        setCurrentChapterIndex(chapterIndex);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      onNavigateToNote: (note) => {
        let chapterIndex = 0;

        if (typeof note.location === 'number') {
          chapterIndex = note.location;
        } else if (typeof note.location === 'string') {
          try {
            // Try to parse as JSON (shadow-dom-range or text-anchor format)
            const locationData = JSON.parse(note.location);
            chapterIndex = locationData.chapterIndex || 0;
          } catch {
            // If not JSON, try to parse as plain number string
            chapterIndex = parseInt(note.location, 10) || 0;
          }
        }

        console.log('Navigating to chapter index:', chapterIndex);
        setCurrentChapterIndex(chapterIndex);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      onNavigateToSearchResult: (result) => {
        const chapterIndex =
          typeof result.location === 'number' ? result.location : 0;
        setCurrentChapterIndex(chapterIndex);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    }),
    [currentChapterIndex, chapters]
  );

  // ============================================================================
  // DEPRECATED: Client-side search fallback function
  // Kept as a separate function for rollback capability if backend search fails
  // ============================================================================
  const onSearchDeprecatedClientSide = useCallback(
    async (query: string): Promise<SearchResult[]> => {
      console.log('⚠️ Using deprecated client-side search for:', query);
      const results: SearchResult[] = [];

      const lowerQuery = query.toLowerCase();

      chapters.forEach((chapter, index) => {
        // Remove HTML tags for search
        const plainText = chapter.content.replace(/<[^>]*>/g, ' ');
        const lowerContent = plainText.toLowerCase();
        let searchIndex = lowerContent.indexOf(lowerQuery);

        while (searchIndex !== -1 && results.length < 50) {
          const start = Math.max(0, searchIndex - 50);
          const end = Math.min(plainText.length, searchIndex + query.length + 50);
          const excerpt = plainText.substring(start, end);

          results.push({
            id: `${index}-${searchIndex}`,
            excerpt: excerpt.trim(),
            location: index,
            locationLabel: chapter.title,
            beforeContext: plainText.substring(
              Math.max(0, searchIndex - 100),
              searchIndex
            ),
            afterContext: plainText.substring(
              searchIndex + query.length,
              Math.min(plainText.length, searchIndex + query.length + 100)
            ),
          });

          searchIndex = lowerContent.indexOf(lowerQuery, searchIndex + 1);
        }
      });

      return results.slice(0, 50);
    },
    [chapters]
  );

  // Reader actions
  const actions: ReaderActions = useMemo(
    () => ({
      onCreateBookmark: async (location: string | number, note?: string) => {
        const chapterIndex =
          typeof location === 'number' ? location : currentChapterIndex;
        const locationLabel = chapters[chapterIndex]?.title || `Chapter ${chapterIndex + 1}`;

        await createBookmark({
          location: chapterIndex,
          locationLabel,
          note,
        });
      },
      onDeleteBookmark: async (bookmarkId: string) => {
        await deleteBookmark(bookmarkId);
      },
      onUpdateBookmark: async (bookmarkId: string, note: string) => {
        await updateBookmark({ id: bookmarkId, note });
      },
      onCreateNote: async (
        location: string | number,
        selectedText: string,
        color: HighlightColor,
        noteText?: string
      ) => {
        // Get serialized TextAnchor (if available from current selection)
        const anchor = getSerializedAnchor();
        const locationToStore = anchor || currentChapterIndex;
        const locationLabel = currentChapter?.title || `Chapter ${currentChapterIndex + 1}`;

        await createHighlight({
          location: locationToStore,
          locationLabel,
          selectedText,
          color,
          noteText,
        });

        // Highlights will be re-applied via useEffect
      },
      onDeleteNote: async (noteId: string) => {
        await deleteHighlight(noteId);
        // TODO: Remove visual highlight from HTML
      },
      onUpdateNote: async (noteId: string, noteText: string) => {
        // Find the note to get its current color
        const note = notes.find(n => n.id === noteId);
        if (note) {
          await updateHighlight({
            id: noteId,
            color: note.color,
            noteText
          });
        }
        // TODO: Update visual highlight in HTML
      },
      // ============================================================================
      // SEARCH: Backend API search using AppBookPageTexts table
      // ============================================================================
      onSearch: async (query: string): Promise<SearchResult[]> => {
        console.log('🔍 Searching book via backend API:', query);

        try {
          // Call backend API to search AppBookPageTexts table
          const response = await booksService.searchInBook(bookId, query, 50);

          // Harvest chapterIndex → secureContentChunkId mappings so the Flashcard
          // Library tab can auto-scope once the user has searched the book at least
          // once. No-op when the response has no results.
          chunkIdTracker.harvestSearchResponse(response);

          console.log('✅ Backend search results:', response.totalMatches, 'matches found');

          // Map backend response to SearchResult[] format
          const results: SearchResult[] = response.results.flatMap(result =>
            result.occurrences.map((occurrence, index) => {
              // Extract before/after context from the full context string
              // Context format: "...before text [MATCH] after text..."
              const contextParts = occurrence.context.split(query);
              const beforeContext = contextParts[0] || '';
              const afterContext = contextParts.slice(1).join(query); // Rejoin in case query appears multiple times

              return {
                id: `${result.secureContentChunkId}-${index}`,
                excerpt: query, // The matched text
                location: result.chapterIndex ?? result.pageNumber ?? 0,
                locationLabel: result.chapterTitle || `Page ${result.pageNumber}` || 'Unknown location',
                beforeContext: beforeContext.trim(),
                afterContext: afterContext.trim(),
              };
            })
          );

          return results;
        } catch (error) {
          showToast('Backend search failed, falling back to client-side search', { type: 'warning' });
          console.error('❌ Backend search failed, falling back to client-side search:', error);
          // Fall back to deprecated client-side search if backend fails
          return onSearchDeprecatedClientSide(query);
        }
      },
    }),
    [
      bookId,
      chapters,
      currentChapterIndex,
      createBookmark,
      deleteBookmark,
      updateBookmark,
      createHighlight,
      deleteHighlight,
      updateHighlight,
      notes,
      onSearchDeprecatedClientSide,
      chunkIdTracker,
    ]
  );

  // Handle adding page-level notes
  const handleAddPageNote = useCallback(
    async (noteText: string, color: HighlightColor) => {
      try {
        const locationLabel = chapters[currentChapterIndex]?.title || `Chapter ${currentChapterIndex + 1}`;
        await createPageNote({
          location: String(currentChapterIndex),
          locationLabel,
          color,
          noteText,
        });
      } catch (error) {
        console.error('Failed to create page note:', error);
        showToast('Failed to create page note. Please try again.', { type: 'error' });
      }
    },
    [createPageNote, currentChapterIndex, chapters]
  );

  // Handle adding bookmark for current page
  const handleAddBookmark = useCallback(
    async () => {
      try {
        const locationLabel = chapters[currentChapterIndex]?.title || `Chapter ${currentChapterIndex + 1}`;
        await createBookmark({
          location: currentChapterIndex,
          locationLabel,
        });
      } catch (error) {
        console.error('Failed to create bookmark:', error);
        showToast('Failed to create bookmark. Please try again.', { type: 'error' });
      }
    },
    [createBookmark, currentChapterIndex, chapters]
  );

  // Memoize onTextSelection to prevent Shadow DOM from being recreated on every render
  const handleTextSelection = useCallback((selectedText: string, range: Range, textContext?: string, targetOffset?: number) => {
    // Calculate and store serialized anchor IMMEDIATELY before selection is cleared
    const hostElement = shadowHostRef.current;
    if (hostElement) {
      // Access the Shadow DOM API exposed by ShadowDOMContentRenderer
      const shadowDOMAPI = (hostElement as any).shadowDOMAPI;
      const shadowRoot = shadowDOMAPI?.getShadowRoot();

      if (shadowRoot) {
        // Get text content from inside the Shadow DOM
        const contentContainer = shadowRoot.querySelector('.content-container');

        if (contentContainer && range) {
          // Calculate offsets by walking through text nodes and matching the Range
          const textContent = contentContainer.textContent || '';

          // Build a list of all text nodes with their cumulative offsets
          const walker = document.createTreeWalker(
            contentContainer,
            NodeFilter.SHOW_TEXT,
            null
          );

          const textNodes: Array<{ node: Text; startOffset: number; endOffset: number }> = [];
          let cumulativeOffset = 0;

          let node: Node | null;
          while ((node = walker.nextNode())) {
            const nodeLength = node.textContent?.length || 0;
            textNodes.push({
              node: node as Text,
              startOffset: cumulativeOffset,
              endOffset: cumulativeOffset + nodeLength
            });
            cumulativeOffset += nodeLength;
          }

          // Find which text nodes contain the range's start and end
          let absoluteStartOffset = -1;
          let absoluteEndOffset = -1;
          let startNode: Node | null = null;
          let startNodeOffset = 0;
          let endNode: Node | null = null;
          let endNodeOffset = 0;

          // Handle range.startContainer - could be text node or element node
          if (range.startContainer.nodeType === Node.TEXT_NODE) {
            // Start container is a text node - find it in our list
            for (const textNodeInfo of textNodes) {
              if (textNodeInfo.node === range.startContainer) {
                absoluteStartOffset = textNodeInfo.startOffset + range.startOffset;
                startNode = textNodeInfo.node;
                startNodeOffset = range.startOffset;
                break;
              }
            }
          } else if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
            // Start container is an element - find first text node within it at the given child index
            // Get all child nodes and use startOffset as child index
            const children = Array.from(range.startContainer.childNodes);

            if (children.length === 0) {
              // Empty container - find first text node in the element
              for (const textNodeInfo of textNodes) {
                if (range.startContainer.contains(textNodeInfo.node)) {
                  absoluteStartOffset = textNodeInfo.startOffset;
                  startNode = textNodeInfo.node;
                  startNodeOffset = 0;
                  break;
                }
              }
            } else {
              const targetChild = children[range.startOffset];

              if (targetChild) {
                // Find this child or its first text node in our list
                for (const textNodeInfo of textNodes) {
                  if (textNodeInfo.node === targetChild) {
                    // Exact match - this text node is the target child
                    absoluteStartOffset = textNodeInfo.startOffset;
                    startNode = textNodeInfo.node;
                    startNodeOffset = 0;
                    break;
                  } else if (targetChild.nodeType === Node.ELEMENT_NODE && targetChild.contains(textNodeInfo.node)) {
                    // Target child is an element containing this text node
                    absoluteStartOffset = textNodeInfo.startOffset;
                    startNode = textNodeInfo.node;
                    startNodeOffset = 0;
                    break;
                  }
                }
              }
            }
          }

          // Handle range.endContainer - could be text node or element node
          if (range.endContainer.nodeType === Node.TEXT_NODE) {
            // End container is a text node - find it in our list
            for (const textNodeInfo of textNodes) {
              if (textNodeInfo.node === range.endContainer) {
                absoluteEndOffset = textNodeInfo.startOffset + range.endOffset;
                endNode = textNodeInfo.node;
                endNodeOffset = range.endOffset;
                break;
              }
            }
          } else if (range.endContainer.nodeType === Node.ELEMENT_NODE) {
            // End container is an element - find last text node before the given child index
            const children = Array.from(range.endContainer.childNodes);

            if (children.length === 0) {
              // Empty container - find last text node in the element
              for (let i = textNodes.length - 1; i >= 0; i--) {
                const textNodeInfo = textNodes[i];
                if (range.endContainer.contains(textNodeInfo.node)) {
                  absoluteEndOffset = textNodeInfo.endOffset;
                  endNode = textNodeInfo.node;
                  endNodeOffset = textNodeInfo.node.textContent?.length || 0;
                  break;
                }
              }
            } else if (range.endOffset === 0) {
              // endOffset is 0 - selection ends before first child
              // This means we want the last text node BEFORE this container
              for (let i = textNodes.length - 1; i >= 0; i--) {
                const textNodeInfo = textNodes[i];
                if (!range.endContainer.contains(textNodeInfo.node)) {
                  // This text node is before the container
                  absoluteEndOffset = textNodeInfo.endOffset;
                  endNode = textNodeInfo.node;
                  endNodeOffset = textNodeInfo.node.textContent?.length || 0;
                  break;
                }
              }
            } else {
              // Normal case: endOffset > 0, find the child at endOffset - 1
              const targetChild = children[range.endOffset - 1]; // endOffset is exclusive, so use -1

              if (targetChild) {
                // Find this child or its last text node in our list
                for (let i = textNodes.length - 1; i >= 0; i--) {
                  const textNodeInfo = textNodes[i];
                  if (textNodeInfo.node === targetChild) {
                    // Exact match - this text node is the target child
                    absoluteEndOffset = textNodeInfo.endOffset;
                    endNode = textNodeInfo.node;
                    endNodeOffset = textNodeInfo.node.textContent?.length || 0;
                    break;
                  } else if (targetChild.nodeType === Node.ELEMENT_NODE && targetChild.contains(textNodeInfo.node)) {
                    // Target child is an element containing this text node
                    absoluteEndOffset = textNodeInfo.endOffset;
                    endNode = textNodeInfo.node;
                    endNodeOffset = textNodeInfo.node.textContent?.length || 0;
                    break;
                  }
                }
              }
            }
          }

          // Create anchor if we successfully calculated offsets
          if (absoluteStartOffset !== -1 && absoluteEndOffset !== -1 && startNode && endNode) {
            const anchor = {
              type: 'shadow-dom-range',
              chapterIndex: currentChapterIndex,
              startOffset: absoluteStartOffset,
              endOffset: absoluteEndOffset,
              prefix: textContent.substring(Math.max(0, absoluteStartOffset - 50), absoluteStartOffset),
              suffix: textContent.substring(absoluteEndOffset, Math.min(textContent.length, absoluteEndOffset + 50))
            };

            const serializedAnchor = JSON.stringify(anchor);
            setShadowSerializedAnchor(serializedAnchor);
          } else {
            // Fallback: Try to find the selected text in the content and calculate offset
            console.warn('⚠️ Could not calculate offsets from Range, trying fallback method:', {
              rangeStartContainer: range.startContainer.nodeName,
              rangeEndContainer: range.endContainer.nodeName,
              rangeStartOffset: range.startOffset,
              rangeEndOffset: range.endOffset,
              selectedText: selectedText.substring(0, 50)
            });

            // Try to find the selected text in the full text content
            console.log('🔍 Attempting fallback search for:', {
              selectedText: selectedText.substring(0, 50),
              selectedTextLength: selectedText.length,
              textContentLength: textContent.length
            });

            // Get approximate position from Range to use as search hint
            let searchStartHint = 0;
            let extractedPrefix = '';
            let exactOffsetFound = false; // Track if we found exact offset via distance matching

            // Use the targetOffset from mouseup to find the right occurrence
            // This is much more reliable than text similarity matching
            if (targetOffset !== undefined && targetOffset >= 0) {
              console.log('📍 Using targetOffset from mouseup to find correct occurrence:', targetOffset);

              // Find all occurrences of the selected text
              const occurrences: number[] = [];
              let searchPos = 0;
              while (searchPos < textContent.length) {
                const foundIndex = textContent.indexOf(selectedText, searchPos);
                if (foundIndex === -1) break;
                occurrences.push(foundIndex);
                searchPos = foundIndex + 1;
              }

              console.log(`Found ${occurrences.length} occurrence(s) of "${selectedText}"`);

              if (occurrences.length > 1) {
                // Multiple occurrences - pick the one CLOSEST to targetOffset
                let bestMatch = occurrences[0];
                let bestDistance = Math.abs(occurrences[0] - targetOffset);

                for (const occurrenceOffset of occurrences) {
                  const distance = Math.abs(occurrenceOffset - targetOffset);

                  console.log(`Occurrence at offset ${occurrenceOffset}:`, {
                    distance: distance,
                    isBetter: distance < bestDistance ? '✅' : '❌'
                  });

                  if (distance < bestDistance) {
                    bestDistance = distance;
                    bestMatch = occurrenceOffset;
                  }
                }

                searchStartHint = bestMatch;
                exactOffsetFound = true; // We have the exact position, don't search again
                console.log('✅ Matched selection to CLOSEST occurrence at offset:', bestMatch, 'distance:', bestDistance);
              } else if (occurrences.length === 1) {
                // Only one occurrence - use it
                searchStartHint = occurrences[0];
                exactOffsetFound = true; // We have the exact position
                console.log('✅ Single occurrence found at offset:', searchStartHint);
              }
            }

            // Fallback: Extract prefix by getting text content before the selection
            if (searchStartHint === 0) {
              try {
                const rangeForPrefix = document.createRange();
                rangeForPrefix.selectNodeContents(contentContainer);
                rangeForPrefix.setEnd(range.startContainer, range.startOffset);
                extractedPrefix = rangeForPrefix.toString();

                console.log('📝 Extracted prefix from DOM:', {
                  prefixLength: extractedPrefix.length,
                  lastPart: extractedPrefix.substring(Math.max(0, extractedPrefix.length - 50))
                });

                // Use prefix length as a strong position hint
                if (extractedPrefix.length > 0) {
                  searchStartHint = extractedPrefix.length;
                  console.log('📍 Using prefix length as search hint:', searchStartHint);
                }
              } catch (e) {
                console.log('Could not extract prefix from Range:', e);
              }
            }

            // Try to estimate position by finding the range's start container in the text nodes
            if (range.startContainer.nodeType === Node.TEXT_NODE && searchStartHint === 0) {
              // If range starts in a text node, find its cumulative offset
              let estimatedOffset = 0;
              for (const textNodeInfo of textNodes) {
                if (textNodeInfo.node === range.startContainer) {
                  estimatedOffset = textNodeInfo.startOffset + range.startOffset;
                  searchStartHint = estimatedOffset;
                  console.log('📍 Estimated position from text node range:', searchStartHint);
                  break;
                }
                // Continue accumulating if not found yet
              }
            } else if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
              // If range starts in an element (DIV), try to estimate based on which text nodes it contains
              const startElement = range.startContainer as Element;

              console.log('Range is in element:', {
                tagName: startElement.tagName,
                startOffset: range.startOffset,
                textNodesCount: textNodes.length
              });

              // Find the first text node that is a descendant of startContainer
              // This gives us an approximate starting position
              // ONLY if we don't already have a hint from text context matching
              if (searchStartHint === 0) {
                for (const textNodeInfo of textNodes) {
                  if (startElement.contains(textNodeInfo.node)) {
                    // This text node is inside the start container
                    // If startOffset > 0, try to find the Nth child
                    searchStartHint = textNodeInfo.startOffset;
                    console.log('📍 Estimated position from element range:', {
                      searchStartHint,
                      textNode: textNodeInfo.node.textContent?.substring(0, 30)
                    });
                    break;
                  }
                }
              } else {
                console.log('✅ Using searchStartHint from text context matching:', searchStartHint);
              }

              // If we still don't have a hint, use the selected text itself to help narrow down
              // Get a unique part of the selected text (first few words)
              if (searchStartHint === 0 && selectedText.length > 3) {
                console.log('⚠️ No position hint from element, will need to use context-aware search');
              }
            }

            // First try exact match near the estimated position
            let selectedTextIndex = -1;
            if (exactOffsetFound) {
              // We already found the exact position via distance-based matching
              // Use it directly without searching again
              selectedTextIndex = searchStartHint;
              console.log('✅ Using exact offset from distance matching:', selectedTextIndex);
            } else if (searchStartHint > 0) {
              // Search within a window around the hint
              const searchRadius = 500;
              const searchStart = Math.max(0, searchStartHint - searchRadius);
              const searchEnd = Math.min(textContent.length, searchStartHint + searchRadius);
              const searchWindow = textContent.substring(searchStart, searchEnd);
              const localIndex = searchWindow.indexOf(selectedText);

              if (localIndex !== -1) {
                selectedTextIndex = searchStart + localIndex;
                console.log('Exact match found near hint position:', selectedTextIndex);
              }
            }

            // If no match near hint, try global exact match
            if (selectedTextIndex === -1) {
              selectedTextIndex = textContent.indexOf(selectedText);
              console.log('Exact match result (global):', selectedTextIndex);
            }

            // If exact match fails, try fuzzy search allowing whitespace differences
            if (selectedTextIndex === -1) {
              console.log('Exact match failed, trying fuzzy search near hint position...');

              // Fuzzy search: walk through content starting near the hint position
              let selIndex = 0;
              let contentIndex = Math.max(0, searchStartHint - 100); // Start a bit before the hint

              while (contentIndex < textContent.length && selIndex < selectedText.length) {
                const contentChar = textContent[contentIndex];
                const selChar = selectedText[selIndex];

                if (contentChar === selChar) {
                  // Exact match
                  if (selIndex === 0) {
                    selectedTextIndex = contentIndex; // Mark start position
                  }
                  selIndex++;
                  contentIndex++;
                } else if (/\s/.test(contentChar) && /\s/.test(selChar)) {
                  // Both are whitespace (even if different types)
                  if (selIndex === 0) {
                    selectedTextIndex = contentIndex;
                  }
                  selIndex++;
                  contentIndex++;
                } else if (/\s/.test(contentChar)) {
                  // Extra whitespace in content, skip it
                  if (selIndex === 0) {
                    // Haven't started matching yet, this whitespace doesn't count
                    contentIndex++;
                  } else {
                    // In the middle of matching, skip content whitespace
                    contentIndex++;
                  }
                } else if (/\s/.test(selChar)) {
                  // Extra whitespace in selection, skip it
                  selIndex++;
                } else {
                  // Characters don't match, reset if we were in the middle of matching
                  if (selIndex > 0) {
                    selIndex = 0;
                    selectedTextIndex = -1;
                  }
                  contentIndex++;
                }
              }

              // Check if we matched the entire selection
              if (selIndex < selectedText.length) {
                selectedTextIndex = -1; // Didn't match everything
                console.log('Fuzzy search failed - only matched', selIndex, 'of', selectedText.length, 'characters');
              } else {
                console.log('Fuzzy search succeeded - found at index:', selectedTextIndex);
              }
            }

            if (selectedTextIndex !== -1) {
              absoluteStartOffset = selectedTextIndex;

              // Calculate end offset by actually measuring the text length in the content
              // Walk through the text content to find where the selection actually ends
              let remainingText = selectedText;
              let searchIndex = selectedTextIndex;
              let matchedChars = 0;

              console.log('Starting character-by-character matching from index:', selectedTextIndex);

              // Match character by character, allowing for whitespace differences
              while (remainingText.length > 0 && searchIndex < textContent.length) {
                const contentChar = textContent[searchIndex];
                const selectedChar = remainingText[0];

                // If characters match (or both are whitespace), advance
                if (contentChar === selectedChar || (/\s/.test(contentChar) && /\s/.test(selectedChar))) {
                  remainingText = remainingText.substring(1);
                  searchIndex++;
                  matchedChars++;
                } else if (/\s/.test(contentChar)) {
                  // Extra whitespace in content, skip it
                  searchIndex++;
                } else if (/\s/.test(selectedChar)) {
                  // Extra whitespace in selection, skip it
                  remainingText = remainingText.substring(1);
                  matchedChars++;
                } else {
                  // Characters don't match, break
                  console.warn('Character mismatch at searchIndex:', searchIndex, 'content:', contentChar, 'selected:', selectedChar);
                  break;
                }
              }

              absoluteEndOffset = searchIndex;

              console.log('Character matching complete:', {
                matchedChars,
                totalChars: selectedText.length,
                remainingText: remainingText.length,
                absoluteStartOffset,
                absoluteEndOffset
              });

              // Find the corresponding text nodes
              let cumulativeOffset = 0;
              for (const textNodeInfo of textNodes) {
                if (cumulativeOffset <= absoluteStartOffset && absoluteStartOffset < cumulativeOffset + textNodeInfo.node.textContent!.length) {
                  startNode = textNodeInfo.node;
                  startNodeOffset = absoluteStartOffset - cumulativeOffset;
                }
                if (cumulativeOffset < absoluteEndOffset && absoluteEndOffset <= cumulativeOffset + textNodeInfo.node.textContent!.length) {
                  endNode = textNodeInfo.node;
                  endNodeOffset = absoluteEndOffset - cumulativeOffset;
                }
                cumulativeOffset += textNodeInfo.node.textContent!.length;
                if (startNode && endNode) break;
              }

              if (startNode && endNode) {
                const anchor = {
                  type: 'shadow-dom-range',
                  chapterIndex: currentChapterIndex,
                  startOffset: absoluteStartOffset,
                  endOffset: absoluteEndOffset,
                  prefix: textContent.substring(Math.max(0, absoluteStartOffset - 50), absoluteStartOffset),
                  suffix: textContent.substring(absoluteEndOffset, Math.min(textContent.length, absoluteEndOffset + 50))
                };

                const serializedAnchor = JSON.stringify(anchor);
                setShadowSerializedAnchor(serializedAnchor);
                console.log('✅ Used fallback method to calculate anchor');
              } else {
                console.error('❌ Fallback method failed - could not find text nodes');
                setShadowSerializedAnchor(null);
              }
            } else {
              console.error('❌ Could not find selected text in content');
              setShadowSerializedAnchor(null);
            }
          }

          // Create a proper range within the Shadow DOM at text node level
          let rect: DOMRect;
          let correctedRange: Range;

          if (startNode && endNode) {
            // Create corrected range from actual text nodes
            correctedRange = document.createRange();
            correctedRange.setStart(startNode, startNodeOffset);
            correctedRange.setEnd(endNode, endNodeOffset);
            rect = correctedRange.getBoundingClientRect();
          } else {
            // Fallback to original range if we couldn't find text nodes
            correctedRange = range;
            rect = range.getBoundingClientRect();
            console.warn('Could not find text nodes for selection, using original range');
          }

          const MENU_HEIGHT = 350;
          const HEADER_HEIGHT = 90;
          const SAFE_MARGIN = 20;

          const spaceAbove = rect.top - HEADER_HEIGHT;
          const hasSpaceAbove = spaceAbove >= (MENU_HEIGHT + SAFE_MARGIN);
          const spaceBelow = window.innerHeight - rect.bottom;
          const hasSpaceBelow = spaceBelow >= (MENU_HEIGHT + SAFE_MARGIN);
          const positionBelow = !hasSpaceAbove || (hasSpaceBelow && spaceBelow > spaceAbove);
          const yPosition = positionBelow ? rect.bottom + 10 : rect.top - 10;

          // NOTE: NOT adding temporary highlight here because it corrupts the DOM
          // and breaks subsequent text selections. The browser's native selection
          // highlight is sufficient until the user confirms they want to create a note.
          // The temp highlight will be added when the user clicks "Add Highlight" button.

          // Set Shadow DOM selection state to trigger menu
          setShadowSelectedText(selectedText);
          setShadowRange(correctedRange);

          // Set menu position. x is the LEFT edge of the selection; the menu
          // aligns its left to x and clamps itself to stay in the viewport.
          // y is the anchor top OR bottom: negative signals "show below"
          // (menu takes Math.abs(y) as the top coord and uses showBelow to
          // decide which vertical edge to pin).
          const menuPos = {
            x: rect.left,
            y: positionBelow ? -yPosition : yPosition,
          };
          setShadowMenuPosition(menuPos);
        } else {
          console.warn('Content container or range not available');
          setShadowSerializedAnchor(null);
        }
      } else {
        console.warn('Shadow root not available for anchor creation');
        setShadowSerializedAnchor(null);
      }
    } else {
      console.warn('Shadow host ref not available for anchor creation');
      setShadowSerializedAnchor(null);
    }
  }, [currentChapterIndex]);

  // Memoize onLinkClick to prevent Shadow DOM from being recreated on every render
  const handleLinkClick = useCallback((href: string, event: Event) => {
    console.log('Link clicked:', href);
    // TODO: Handle internal chapter navigation
    event.preventDefault();
  }, []);

  // Memoize onReady to prevent Shadow DOM from being recreated on every render
  const handleShadowDOMReady = useCallback(() => {
    setIsShadowDOMReady(true);
  }, []);

  // Show loading while chapter list is loading OR chapter index hasn't been initialized yet
  if (isLoading || currentChapterIndex < 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-900">
        <LoadingSpinner />
      </div>
    );
  }

  // Gate content behind DRM initialization when DRM is enabled
  // This ensures security protections are fully active before content is visible
  // Skip DRM session check when offline - content will be loaded from IndexedDB
  if (isDrmEnabled && drmContext && !drmContext.session && navigator.onLine) {
    // Show error if session initialization failed
    if (drmContext.error) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-neutral-900 text-white">
          <div className="text-error-500 text-6xl mb-4">⚠️</div>
          <p className="text-xl font-semibold mb-2">Failed to Initialize Secure Session</p>
          <p className="text-sm text-neutral-400 mb-6 max-w-md text-center">{drmContext.error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      );
    }

    // Show loading while initializing
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-neutral-900 text-white">
        <LoadingSpinner />
        <p className="mt-4 text-lg">Initializing secure session...</p>
        <p className="mt-2 text-sm text-neutral-400">Setting up DRM protections</p>
      </div>
    );
  }

  return (
    <UnifiedReaderLayout
      bookId={bookId}
      format={BookFormat.CustomHtml}
      metadata={metadata}
      navigation={navigation}
      navigationCallbacks={navigationCallbacks}
      tableOfContents={tableOfContents}
      bookmarks={bookmarks}
      notes={notes}
      actions={actions}
      onAddPageNote={handleAddPageNote}
      theme={settings?.theme}
      fontSize={settings?.fontSize}
      fontFamily={settings?.fontFamily}
      activeSecureContentChunkId={activeSecureContentChunkId}
    >
      <div className="relative w-full h-full">
        <PageTransition animation={pageAnimation} pageKey={currentChapterIndex}>
          <div
            ref={containerRef}
            className="w-full h-full overflow-auto"
            style={{
              backgroundColor: settings?.backgroundColor || '#FFFFFF',
              color: settings?.textColor || '#000000',
              padding: marginPadding,
            }}
          >
            {currentChapter ? (
              <ShadowDOMContentRenderer
                ref={shadowHostRef}
                htmlContent={chapterHTML}
                cssContent={chapterCSS}
                stylesheetUrls={chapterStylesheetUrls}
                onLinkClick={handleLinkClick}
                onTextSelection={handleTextSelection}
                onReady={handleShadowDOMReady}
                allowSelection={true}
                className="prose prose-lg max-w-none w-full"
                theme={settings?.theme || 'light'}
                fontSize={settings?.fontSize}
                fontFamily={settings?.fontFamily}
                lineHeight={settings?.lineHeight}
                backgroundColor={settings?.backgroundColor}
                textColor={settings?.textColor}
                useBookStyles={settings?.useBookStyles ?? true}
                columnCount={effectiveLayout === LayoutMode.Spread ? 2 : 1}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-500">
                <p>{t('App.Reader.NoContentAvailable')}</p>
              </div>
            )}
          </div>
        </PageTransition>
      </div>

      {/* Text Selection Menu with Flashcard Support */}
      {shadowSelectedText && shadowMenuPosition && (
        <TextSelectionMenuWithFlashcard
          selectedText={shadowSelectedText}
          selection={window.getSelection()}
          menuPosition={shadowMenuPosition}
          bookMetadata={metadata}
          locationLabel={currentChapter?.title || `Chapter ${currentChapterIndex + 1}`}
          onCreateHighlight={async (color, noteText) => {
            const anchor = getSerializedAnchor();
            if (!anchor) {
              console.error('Failed to get serialized anchor');
              return;
            }

            const locationLabel = currentChapter?.title || `Chapter ${currentChapterIndex + 1}`;

            await createHighlight({
              location: anchor,
              locationLabel,
              selectedText,
              color,
              noteText,
            });

            clearSelection();
          }}
          onCreateFlashcard={async (question, answer, sourceText) => {
            const anchor = getSerializedAnchor();
            if (!anchor) {
              console.error('Failed to get serialized anchor');
              return;
            }

            // Ensure we have a deck
            let deckId = currentDeckId;
            if (!deckId) {
              // Create default deck for this book
              const defaultDeck = await createDeck({
                bookId,
                name: `${metadata?.title || 'Book'} Flashcards`,
                description: 'Flashcards created from highlights',
              });
              deckId = defaultDeck.id;
              setCurrentDeckId(deckId);
            }

            const locationLabel = currentChapter?.title || `Chapter ${currentChapterIndex + 1}`;

            await createFlashcard({
              deckId,
              question,
              answer,
              location: anchor,
              locationLabel,
              sourceText,
            });

            clearSelection();
          }}
          onClose={clearSelection}
        />
      )}

      {/* Highlight Note Modal */}
      {selectedHighlight && (
        <HighlightNoteModal
          note={selectedHighlight}
          bookMetadata={metadata}
          onClose={() => setSelectedHighlight(null)}
          onUpdate={async (id, color, noteText) => {
            await updateHighlight({
              id,
              color,
              noteText,
            });
            setSelectedHighlight(null);
          }}
          onDelete={async () => {
            await deleteHighlight(selectedHighlight.id);
            setSelectedHighlight(null);
          }}
        />
      )}
    </UnifiedReaderLayout>
  );
}

/**
 * Unified CustomHTML Renderer - DRM-aware wrapper
 *
 * This component automatically detects DRM status and wraps the reader
 * in SecureReadingProvider when DRM is enabled. For non-DRM books,
 * it renders the reader directly without DRM overhead.
 *
 * Features (available for ALL books):
 * - Table of Contents navigation
 * - Highlights with color picker and text anchors
 * - Notes and annotations
 * - Bookmarks
 * - Flashcard creation from highlights
 * - Reading progress tracking
 * - Reader settings (theme, font, etc.)
 * - Shadow DOM rendering for style isolation
 *
 * DRM Features (only when isDrmEnabled):
 * - DRM quota tracking and display
 * - Content protection (no right-click, text selection, print)
 * - DevTools detection
 * - Session management with heartbeat
 */
export default function UnifiedCustomHtmlRenderer({ bookId, isDrmEnabled = false }: UnifiedCustomHtmlRendererProps) {
  // If DRM is enabled, wrap in SecureReadingProvider
  if (isDrmEnabled) {
    return (
      <SecureReadingProvider>
        <UnifiedCustomHtmlRendererInternal bookId={bookId} isDrmEnabled={true} />
      </SecureReadingProvider>
    );
  }

  // Non-DRM books render directly
  return <UnifiedCustomHtmlRendererInternal bookId={bookId} isDrmEnabled={false} />;
}
